import { Topic } from '../../../grades/components/GradeLessonPage';

export const elementaryAlgebraTopic: Topic = {
  id: 'elementary-algebra',
  title: 'Elementary Algebra',
  icon: 'ri-function-line',
  color: 'violet',
  skills: [
    {
      id: 'substitution-evaluating',
      title: 'Substitution & Evaluating Expressions',
      standards: ['ACT Math: Elementary Algebra', 'Algebra'],
      explanation: 'Substitution involves replacing variables with given values and evaluating the resulting expression. This fundamental skill requires careful attention to order of operations and proper handling of negative numbers and exponents. The ACT tests your ability to substitute values into algebraic expressions, formulas, and functions, then simplify to find the result. This skill is essential for working with formulas in science, finance, and real-world problem-solving.',
      questions: [
        {
          question: 'Evaluate 3x² - 2x + 5 when x = 4',
          solution: 'Step 1: Substitute x = 4 into expression\nStep 2: 3(4)² - 2(4) + 5\nStep 3: Calculate exponent: 3(16) - 2(4) + 5\nStep 4: Multiply: 48 - 8 + 5\nStep 5: Add/subtract left to right: 48 - 8 = 40, then 40 + 5 = 45',
          answer: '45'
        },
        {
          question: 'If a = -3 and b = 5, find 2a² + 3b',
          solution: 'Step 1: Substitute values: 2(-3)² + 3(5)\nStep 2: Calculate exponent: (-3)² = 9\nStep 3: 2(9) + 3(5)\nStep 4: Multiply: 18 + 15\nStep 5: Add: 33',
          answer: '33'
        },
        {
          question: 'Evaluate (x + y)/(x - y) when x = 8 and y = 2',
          solution: 'Step 1: Substitute values: (8 + 2)/(8 - 2)\nStep 2: Simplify numerator: 10\nStep 3: Simplify denominator: 6\nStep 4: Divide: 10/6 = 5/3',
          answer: '5/3 or 1 2/3'
        },
        {
          question: 'If n = -2, what is -n³ + 4n?',
          solution: 'Step 1: Substitute n = -2: -(-2)³ + 4(-2)\nStep 2: Calculate exponent: (-2)³ = -8\nStep 3: Apply negative: -(-8) = 8\nStep 4: Calculate second term: 4(-2) = -8\nStep 5: Add: 8 + (-8) = 0',
          answer: '0'
        },
        {
          question: 'Evaluate |2x - 5| when x = 1',
          solution: 'Step 1: Substitute x = 1: |2(1) - 5|\nStep 2: Multiply: |2 - 5|\nStep 3: Subtract: |-3|\nStep 4: Absolute value: 3',
          answer: '3'
        },
        {
          question: 'If p = 6 and q = -4, find p² - q²',
          solution: 'Step 1: Substitute: (6)² - (-4)²\nStep 2: Calculate exponents: 36 - 16\nStep 3: Subtract: 20',
          answer: '20'
        },
        {
          question: 'Evaluate 5(a - b) + 2ab when a = 3 and b = -2',
          solution: 'Step 1: Substitute: 5(3 - (-2)) + 2(3)(-2)\nStep 2: Simplify parentheses: 5(3 + 2) + 2(3)(-2)\nStep 3: 5(5) + 2(3)(-2)\nStep 4: Multiply: 25 + (-12)\nStep 5: Add: 13',
          answer: '13'
        },
        {
          question: 'If x = 1/2, what is 4x² + 8x?',
          solution: 'Step 1: Substitute: 4(1/2)² + 8(1/2)\nStep 2: Calculate exponent: (1/2)² = 1/4\nStep 3: 4(1/4) + 8(1/2)\nStep 4: Multiply: 1 + 4\nStep 5: Add: 5',
          answer: '5'
        },
        {
          question: 'Evaluate (2m + n)² when m = 3 and n = -1',
          solution: 'Step 1: Substitute: (2(3) + (-1))²\nStep 2: Simplify inside: (6 - 1)²\nStep 3: (5)²\nStep 4: Calculate: 25',
          answer: '25'
        },
        {
          question: 'If t = -5, find 3t² - 2t + 1',
          solution: 'Step 1: Substitute: 3(-5)² - 2(-5) + 1\nStep 2: Calculate exponent: 3(25) - 2(-5) + 1\nStep 3: Multiply: 75 + 10 + 1\nStep 4: Add: 86',
          answer: '86'
        }
      ]
    },
    {
      id: 'setting-up-equations',
      title: 'Setting Up Equations from Word Problems',
      standards: ['ACT Math: Elementary Algebra', 'Algebra'],
      explanation: 'Translating word problems into algebraic equations is a critical skill for solving real-world problems. This involves identifying the unknown quantity, choosing a variable to represent it, and writing an equation that represents the relationships described in the problem. The ACT tests your ability to interpret verbal descriptions and convert them into mathematical expressions and equations. Key phrases like "sum," "difference," "product," "quotient," "is," "more than," and "less than" have specific mathematical meanings.',
      questions: [
        {
          question: 'A number increased by 7 equals 23. Write an equation.',
          solution: 'Step 1: Let x = the unknown number\nStep 2: "Increased by 7" means add 7: x + 7\nStep 3: "Equals 23" means = 23\nStep 4: Equation: x + 7 = 23',
          answer: 'x + 7 = 23'
        },
        {
          question: 'Three times a number minus 5 is 16. Write an equation.',
          solution: 'Step 1: Let n = the number\nStep 2: "Three times a number" means 3n\nStep 3: "Minus 5" means subtract 5: 3n - 5\nStep 4: "Is 16" means = 16\nStep 5: Equation: 3n - 5 = 16',
          answer: '3n - 5 = 16'
        },
        {
          question: 'The sum of two consecutive integers is 47. Write an equation.',
          solution: 'Step 1: Let x = first integer\nStep 2: Next consecutive integer = x + 1\nStep 3: "Sum" means add: x + (x + 1)\nStep 4: "Is 47" means = 47\nStep 5: Equation: x + (x + 1) = 47 or 2x + 1 = 47',
          answer: 'x + (x + 1) = 47'
        },
        {
          question: 'Sarah has 5 more books than Tom. Together they have 23 books. Write an equation for Tom\'s books.',
          solution: 'Step 1: Let t = Tom\'s books\nStep 2: Sarah has 5 more: t + 5\nStep 3: Together means add: t + (t + 5)\nStep 4: Total is 23: t + (t + 5) = 23\nStep 5: Simplified: 2t + 5 = 23',
          answer: '2t + 5 = 23'
        },
        {
          question: 'A rectangle\'s length is 3 times its width. The perimeter is 48. Write an equation.',
          solution: 'Step 1: Let w = width\nStep 2: Length = 3w\nStep 3: Perimeter formula: P = 2l + 2w\nStep 4: Substitute: 48 = 2(3w) + 2w\nStep 5: Equation: 48 = 6w + 2w or 48 = 8w',
          answer: '48 = 8w'
        },
        {
          question: 'The product of a number and 6, decreased by 8, equals 34. Write an equation.',
          solution: 'Step 1: Let x = the number\nStep 2: "Product of number and 6" means 6x\nStep 3: "Decreased by 8" means subtract 8: 6x - 8\nStep 4: "Equals 34" means = 34\nStep 5: Equation: 6x - 8 = 34',
          answer: '6x - 8 = 34'
        },
        {
          question: 'A number divided by 4, plus 7, equals 15. Write an equation.',
          solution: 'Step 1: Let n = the number\nStep 2: "Divided by 4" means n/4\nStep 3: "Plus 7" means add 7: n/4 + 7\nStep 4: "Equals 15" means = 15\nStep 5: Equation: n/4 + 7 = 15',
          answer: 'n/4 + 7 = 15'
        },
        {
          question: 'Five less than twice a number is 19. Write an equation.',
          solution: 'Step 1: Let x = the number\nStep 2: "Twice a number" means 2x\nStep 3: "Five less than" means subtract 5 from 2x: 2x - 5\nStep 4: "Is 19" means = 19\nStep 5: Equation: 2x - 5 = 19',
          answer: '2x - 5 = 19'
        },
        {
          question: 'The sum of three consecutive even integers is 72. Write an equation.',
          solution: 'Step 1: Let x = first even integer\nStep 2: Second even integer = x + 2\nStep 3: Third even integer = x + 4\nStep 4: Sum: x + (x + 2) + (x + 4) = 72\nStep 5: Simplified: 3x + 6 = 72',
          answer: '3x + 6 = 72'
        },
        {
          question: 'A shirt costs $5 less than twice the cost of a hat. Together they cost $40. Write an equation for the hat.',
          solution: 'Step 1: Let h = cost of hat\nStep 2: Shirt costs "twice hat minus 5": 2h - 5\nStep 3: Together: h + (2h - 5) = 40\nStep 4: Simplified: 3h - 5 = 40',
          answer: '3h - 5 = 40'
        }
      ]
    },
    {
      id: 'linear-equations-one-variable',
      title: 'Linear Equations in One Variable',
      standards: ['ACT Math: Elementary Algebra', 'Algebra'],
      explanation: 'Linear equations in one variable are equations that can be written in the form ax + b = c, where a, b, and c are constants and a ≠ 0. Solving these equations involves using inverse operations to isolate the variable. The ACT tests multi-step equations requiring the distributive property, combining like terms, and working with fractions and decimals. The goal is always to get the variable alone on one side of the equation.',
      questions: [
        {
          question: 'Solve: 5x - 8 = 27',
          solution: 'Step 1: Add 8 to both sides: 5x - 8 + 8 = 27 + 8\nStep 2: 5x = 35\nStep 3: Divide both sides by 5: x = 35/5\nStep 4: x = 7',
          answer: 'x = 7'
        },
        {
          question: 'Solve: 3(x + 4) = 21',
          solution: 'Step 1: Distribute: 3x + 12 = 21\nStep 2: Subtract 12 from both sides: 3x = 9\nStep 3: Divide by 3: x = 3',
          answer: 'x = 3'
        },
        {
          question: 'Solve: 2x + 5 = 4x - 11',
          solution: 'Step 1: Subtract 2x from both sides: 5 = 2x - 11\nStep 2: Add 11 to both sides: 16 = 2x\nStep 3: Divide by 2: 8 = x\nStep 4: x = 8',
          answer: 'x = 8'
        },
        {
          question: 'Solve: (x/3) + 7 = 12',
          solution: 'Step 1: Subtract 7 from both sides: x/3 = 5\nStep 2: Multiply both sides by 3: x = 15',
          answer: 'x = 15'
        },
        {
          question: 'Solve: 4(2x - 3) = 5(x + 1)',
          solution: 'Step 1: Distribute left: 8x - 12 = 5(x + 1)\nStep 2: Distribute right: 8x - 12 = 5x + 5\nStep 3: Subtract 5x: 3x - 12 = 5\nStep 4: Add 12: 3x = 17\nStep 5: Divide by 3: x = 17/3',
          answer: 'x = 17/3 or 5 2/3'
        },
        {
          question: 'Solve: 0.5x + 3 = 8',
          solution: 'Step 1: Subtract 3 from both sides: 0.5x = 5\nStep 2: Divide by 0.5 (or multiply by 2): x = 10',
          answer: 'x = 10'
        },
        {
          question: 'Solve: 7 - 2x = 15',
          solution: 'Step 1: Subtract 7 from both sides: -2x = 8\nStep 2: Divide by -2: x = -4',
          answer: 'x = -4'
        },
        {
          question: 'Solve: (2x + 1)/4 = 3',
          solution: 'Step 1: Multiply both sides by 4: 2x + 1 = 12\nStep 2: Subtract 1: 2x = 11\nStep 3: Divide by 2: x = 11/2 or 5.5',
          answer: 'x = 11/2 or 5.5'
        },
        {
          question: 'Solve: 6x - (2x + 5) = 11',
          solution: 'Step 1: Distribute negative: 6x - 2x - 5 = 11\nStep 2: Combine like terms: 4x - 5 = 11\nStep 3: Add 5: 4x = 16\nStep 4: Divide by 4: x = 4',
          answer: 'x = 4'
        },
        {
          question: 'Solve: 3(x - 2) + 2(x + 1) = 14',
          solution: 'Step 1: Distribute: 3x - 6 + 2x + 2 = 14\nStep 2: Combine like terms: 5x - 4 = 14\nStep 3: Add 4: 5x = 18\nStep 4: Divide by 5: x = 18/5 or 3.6',
          answer: 'x = 18/5 or 3.6'
        }
      ]
    },
    {
      id: 'absolute-value-equations',
      title: 'Absolute Value Equations',
      standards: ['ACT Math: Elementary Algebra', 'Algebra'],
      explanation: 'Absolute value represents the distance from zero on a number line, always resulting in a non-negative value. Equations involving absolute value typically have two solutions because both a positive and negative number can have the same absolute value. To solve |x| = a (where a ≥ 0), we write two equations: x = a or x = -a. The ACT tests basic absolute value equations and those requiring algebraic manipulation before applying the absolute value property.',
      questions: [
        {
          question: 'Solve: |x| = 9',
          solution: 'Step 1: Absolute value equation has two cases\nStep 2: Case 1: x = 9\nStep 3: Case 2: x = -9\nStep 4: Solutions: x = 9 or x = -9',
          answer: 'x = 9 or x = -9'
        },
        {
          question: 'Solve: |x + 3| = 7',
          solution: 'Step 1: Set up two equations\nStep 2: Case 1: x + 3 = 7, so x = 4\nStep 3: Case 2: x + 3 = -7, so x = -10\nStep 4: Solutions: x = 4 or x = -10',
          answer: 'x = 4 or x = -10'
        },
        {
          question: 'Solve: |2x - 5| = 11',
          solution: 'Step 1: Case 1: 2x - 5 = 11\nStep 2: 2x = 16, so x = 8\nStep 3: Case 2: 2x - 5 = -11\nStep 4: 2x = -6, so x = -3\nStep 5: Solutions: x = 8 or x = -3',
          answer: 'x = 8 or x = -3'
        },
        {
          question: 'Solve: |x - 4| = 0',
          solution: 'Step 1: Absolute value equals 0 only when expression inside equals 0\nStep 2: x - 4 = 0\nStep 3: x = 4\nStep 4: Only one solution',
          answer: 'x = 4'
        },
        {
          question: 'Solve: 3|x| = 15',
          solution: 'Step 1: Divide both sides by 3: |x| = 5\nStep 2: Case 1: x = 5\nStep 3: Case 2: x = -5\nStep 4: Solutions: x = 5 or x = -5',
          answer: 'x = 5 or x = -5'
        },
        {
          question: 'Solve: |x + 2| - 3 = 4',
          solution: 'Step 1: Add 3 to both sides: |x + 2| = 7\nStep 2: Case 1: x + 2 = 7, so x = 5\nStep 3: Case 2: x + 2 = -7, so x = -9\nStep 4: Solutions: x = 5 or x = -9',
          answer: 'x = 5 or x = -9'
        },
        {
          question: 'Solve: |3x + 1| = 10',
          solution: 'Step 1: Case 1: 3x + 1 = 10\nStep 2: 3x = 9, so x = 3\nStep 3: Case 2: 3x + 1 = -10\nStep 4: 3x = -11, so x = -11/3\nStep 5: Solutions: x = 3 or x = -11/3',
          answer: 'x = 3 or x = -11/3'
        },
        {
          question: 'Solve: |x| + 5 = 12',
          solution: 'Step 1: Subtract 5 from both sides: |x| = 7\nStep 2: Case 1: x = 7\nStep 3: Case 2: x = -7\nStep 4: Solutions: x = 7 or x = -7',
          answer: 'x = 7 or x = -7'
        },
        {
          question: 'Solve: |5 - x| = 8',
          solution: 'Step 1: Case 1: 5 - x = 8\nStep 2: -x = 3, so x = -3\nStep 3: Case 2: 5 - x = -8\nStep 4: -x = -13, so x = 13\nStep 5: Solutions: x = -3 or x = 13',
          answer: 'x = -3 or x = 13'
        },
        {
          question: 'Solve: 2|x - 1| = 14',
          solution: 'Step 1: Divide by 2: |x - 1| = 7\nStep 2: Case 1: x - 1 = 7, so x = 8\nStep 3: Case 2: x - 1 = -7, so x = -6\nStep 4: Solutions: x = 8 or x = -6',
          answer: 'x = 8 or x = -6'
        }
      ]
    },
    {
      id: 'inequalities',
      title: 'Linear Inequalities',
      standards: ['ACT Math: Elementary Algebra', 'Algebra'],
      explanation: 'Inequalities compare two expressions using symbols: < (less than), > (greater than), ≤ (less than or equal to), ≥ (greater than or equal to). Solving inequalities is similar to solving equations, with one critical difference: when multiplying or dividing both sides by a negative number, the inequality sign must be reversed. The ACT tests one-variable inequalities, compound inequalities, and graphing solutions on a number line.',
      questions: [
        {
          question: 'Solve: 3x + 5 > 14',
          solution: 'Step 1: Subtract 5 from both sides: 3x > 9\nStep 2: Divide by 3: x > 3\nStep 3: Solution: all numbers greater than 3',
          answer: 'x > 3'
        },
        {
          question: 'Solve: -2x ≤ 10',
          solution: 'Step 1: Divide both sides by -2\nStep 2: REVERSE inequality sign: x ≥ -5\nStep 3: Solution: all numbers greater than or equal to -5',
          answer: 'x ≥ -5'
        },
        {
          question: 'Solve: 5 - 2x < 11',
          solution: 'Step 1: Subtract 5 from both sides: -2x < 6\nStep 2: Divide by -2 and reverse sign: x > -3\nStep 3: Solution: all numbers greater than -3',
          answer: 'x > -3'
        },
        {
          question: 'Solve: 4(x - 3) ≥ 8',
          solution: 'Step 1: Distribute: 4x - 12 ≥ 8\nStep 2: Add 12: 4x ≥ 20\nStep 3: Divide by 4: x ≥ 5',
          answer: 'x ≥ 5'
        },
        {
          question: 'Solve: -3x + 7 > -5',
          solution: 'Step 1: Subtract 7: -3x > -12\nStep 2: Divide by -3 and reverse sign: x < 4',
          answer: 'x < 4'
        },
        {
          question: 'Solve: 2x + 3 ≤ 5x - 9',
          solution: 'Step 1: Subtract 2x from both sides: 3 ≤ 3x - 9\nStep 2: Add 9: 12 ≤ 3x\nStep 3: Divide by 3: 4 ≤ x\nStep 4: Rewrite: x ≥ 4',
          answer: 'x ≥ 4'
        },
        {
          question: 'Solve: (x/2) - 3 < 5',
          solution: 'Step 1: Add 3 to both sides: x/2 < 8\nStep 2: Multiply by 2: x < 16',
          answer: 'x < 16'
        },
        {
          question: 'Solve: -5(x + 2) > 15',
          solution: 'Step 1: Distribute: -5x - 10 > 15\nStep 2: Add 10: -5x > 25\nStep 3: Divide by -5 and reverse: x < -5',
          answer: 'x < -5'
        },
        {
          question: 'Solve compound inequality: -3 < 2x + 1 < 9',
          solution: 'Step 1: Subtract 1 from all parts: -4 < 2x < 8\nStep 2: Divide all by 2: -2 < x < 4\nStep 3: Solution: x is between -2 and 4',
          answer: '-2 < x < 4'
        },
        {
          question: 'Solve: 3(x - 4) + 2 ≤ 5',
          solution: 'Step 1: Distribute: 3x - 12 + 2 ≤ 5\nStep 2: Combine: 3x - 10 ≤ 5\nStep 3: Add 10: 3x ≤ 15\nStep 4: Divide by 3: x ≤ 5',
          answer: 'x ≤ 5'
        }
      ]
    },
    {
      id: 'linear-equations-two-variables',
      title: 'Linear Equations in Two Variables',
      standards: ['ACT Math: Elementary Algebra', 'Algebra'],
      explanation: 'Linear equations in two variables have the form ax + by = c or y = mx + b (slope-intercept form). These equations represent straight lines when graphed on a coordinate plane. The ACT tests your ability to identify slope and y-intercept, convert between different forms, find x and y intercepts, and understand parallel and perpendicular lines. The slope m represents the rate of change, and the y-intercept b is where the line crosses the y-axis.',
      questions: [
        {
          question: 'What is the slope of the line y = 3x - 7?',
          solution: 'Step 1: Identify slope-intercept form: y = mx + b\nStep 2: Compare: y = 3x - 7\nStep 3: Slope m = 3\nStep 4: Y-intercept b = -7',
          answer: 'Slope = 3'
        },
        {
          question: 'Find the y-intercept of 2x + 3y = 12',
          solution: 'Step 1: Y-intercept occurs when x = 0\nStep 2: Substitute x = 0: 2(0) + 3y = 12\nStep 3: 3y = 12\nStep 4: y = 4\nStep 5: Y-intercept: (0, 4)',
          answer: '(0, 4) or y = 4'
        },
        {
          question: 'Convert to slope-intercept form: 4x - 2y = 8',
          solution: 'Step 1: Solve for y: -2y = -4x + 8\nStep 2: Divide by -2: y = 2x - 4\nStep 3: Slope = 2, y-intercept = -4',
          answer: 'y = 2x - 4'
        },
        {
          question: 'Find the x-intercept of y = -2x + 6',
          solution: 'Step 1: X-intercept occurs when y = 0\nStep 2: Substitute: 0 = -2x + 6\nStep 3: 2x = 6\nStep 4: x = 3\nStep 5: X-intercept: (3, 0)',
          answer: '(3, 0) or x = 3'
        },
        {
          question: 'What is the slope of a line parallel to y = -1/2x + 5?',
          solution: 'Step 1: Parallel lines have equal slopes\nStep 2: Given line has slope = -1/2\nStep 3: Parallel line also has slope = -1/2',
          answer: 'Slope = -1/2'
        },
        {
          question: 'Write equation of line with slope 4 passing through (0, -3)',
          solution: 'Step 1: Use slope-intercept form: y = mx + b\nStep 2: Given: m = 4, point (0, -3) is y-intercept\nStep 3: b = -3\nStep 4: Equation: y = 4x - 3',
          answer: 'y = 4x - 3'
        },
        {
          question: 'Find slope of line through points (2, 5) and (6, 13)',
          solution: 'Step 1: Use slope formula: m = (y₂ - y₁)/(x₂ - x₁)\nStep 2: m = (13 - 5)/(6 - 2)\nStep 3: m = 8/4\nStep 4: m = 2',
          answer: 'Slope = 2'
        },
        {
          question: 'What is the slope of a line perpendicular to y = 3x + 1?',
          solution: 'Step 1: Perpendicular slopes are negative reciprocals\nStep 2: Given slope = 3 = 3/1\nStep 3: Negative reciprocal = -1/3',
          answer: 'Slope = -1/3'
        },
        {
          question: 'Does point (4, 7) lie on the line y = 2x - 1?',
          solution: 'Step 1: Substitute x = 4 into equation\nStep 2: y = 2(4) - 1\nStep 3: y = 8 - 1 = 7\nStep 4: Point gives y = 7, equation gives y = 7 ✓\nStep 5: Yes, point lies on the line',
          answer: 'Yes'
        },
        {
          question: 'Write equation in standard form: y = -3x + 9',
          solution: 'Step 1: Standard form is Ax + By = C\nStep 2: Add 3x to both sides: 3x + y = 9\nStep 3: Standard form: 3x + y = 9',
          answer: '3x + y = 9'
        }
      ]
    },
    {
      id: 'systems-of-equations',
      title: 'Systems of Linear Equations',
      standards: ['ACT Math: Elementary Algebra', 'Algebra'],
      explanation: 'A system of linear equations consists of two or more equations with the same variables. The solution is the point(s) where the lines intersect. The ACT tests three methods for solving systems: substitution (solve one equation for a variable and substitute into the other), elimination (add or subtract equations to eliminate a variable), and graphing. Systems can have one solution (lines intersect), no solution (parallel lines), or infinitely many solutions (same line).',
      questions: [
        {
          question: 'Solve by substitution: y = 2x and x + y = 9',
          solution: 'Step 1: Substitute y = 2x into second equation\nStep 2: x + 2x = 9\nStep 3: 3x = 9, so x = 3\nStep 4: Substitute back: y = 2(3) = 6\nStep 5: Solution: (3, 6)',
          answer: '(3, 6)'
        },
        {
          question: 'Solve by elimination: 2x + y = 10 and 3x - y = 5',
          solution: 'Step 1: Add equations to eliminate y:\n(2x + y) + (3x - y) = 10 + 5\nStep 2: 5x = 15\nStep 3: x = 3\nStep 4: Substitute into first: 2(3) + y = 10\nStep 5: 6 + y = 10, so y = 4\nSolution: (3, 4)',
          answer: '(3, 4)'
        },
        {
          question: 'Solve: x + 2y = 8 and x = 4',
          solution: 'Step 1: Substitute x = 4 into first equation\nStep 2: 4 + 2y = 8\nStep 3: 2y = 4\nStep 4: y = 2\nStep 5: Solution: (4, 2)',
          answer: '(4, 2)'
        },
        {
          question: 'Solve by elimination: 3x + 2y = 12 and 3x - 2y = 6',
          solution: 'Step 1: Add equations:\n(3x + 2y) + (3x - 2y) = 12 + 6\nStep 2: 6x = 18\nStep 3: x = 3\nStep 4: Substitute: 3(3) + 2y = 12\nStep 5: 9 + 2y = 12, so 2y = 3, y = 3/2\nSolution: (3, 3/2)',
          answer: '(3, 3/2) or (3, 1.5)'
        },
        {
          question: 'Solve: y = x + 1 and y = 2x - 3',
          solution: 'Step 1: Set equations equal: x + 1 = 2x - 3\nStep 2: Subtract x: 1 = x - 3\nStep 3: Add 3: x = 4\nStep 4: Substitute: y = 4 + 1 = 5\nStep 5: Solution: (4, 5)',
          answer: '(4, 5)'
        },
        {
          question: 'Solve: 2x + 3y = 7 and x - y = 1',
          solution: 'Step 1: From second equation: x = y + 1\nStep 2: Substitute into first: 2(y + 1) + 3y = 7\nStep 3: 2y + 2 + 3y = 7\nStep 4: 5y = 5, so y = 1\nStep 5: x = 1 + 1 = 2\nSolution: (2, 1)',
          answer: '(2, 1)'
        },
        {
          question: 'Solve by elimination: 4x + 2y = 14 and 2x + y = 7',
          solution: 'Step 1: Multiply second equation by -2: -4x - 2y = -14\nStep 2: Add to first: (4x + 2y) + (-4x - 2y) = 14 + (-14)\nStep 3: 0 = 0 (always true)\nStep 4: This means infinitely many solutions\nStep 5: Equations represent the same line',
          answer: 'Infinitely many solutions'
        },
        {
          question: 'Solve: x + y = 5 and 2x + 2y = 8',
          solution: 'Step 1: Multiply first by -2: -2x - 2y = -10\nStep 2: Add to second: (2x + 2y) + (-2x - 2y) = 8 + (-10)\nStep 3: 0 = -2 (false)\nStep 4: No solution - lines are parallel',
          answer: 'No solution'
        },
        {
          question: 'Solve: 5x - 2y = 4 and x + 2y = 8',
          solution: 'Step 1: Add equations to eliminate y:\n(5x - 2y) + (x + 2y) = 4 + 8\nStep 2: 6x = 12\nStep 3: x = 2\nStep 4: Substitute: 2 + 2y = 8\nStep 5: 2y = 6, so y = 3\nSolution: (2, 3)',
          answer: '(2, 3)'
        },
        {
          question: 'Solve: y = 3x - 2 and 6x - 2y = 4',
          solution: 'Step 1: Substitute first into second: 6x - 2(3x - 2) = 4\nStep 2: 6x - 6x + 4 = 4\nStep 3: 4 = 4 (always true)\nStep 4: Infinitely many solutions\nStep 5: Same line in different forms',
          answer: 'Infinitely many solutions'
        }
      ]
    },
    {
      id: 'polynomials',
      title: 'Polynomials: Add, Subtract, Multiply',
      standards: ['ACT Math: Elementary Algebra', 'Algebra'],
      explanation: 'Polynomials are expressions with variables raised to whole number exponents, combined using addition, subtraction, and multiplication. Terms are combined by adding or subtracting coefficients of like terms (same variable with same exponent). The ACT tests adding and subtracting polynomials (combine like terms), multiplying monomials (multiply coefficients and add exponents), and multiplying polynomials using the distributive property and FOIL method for binomials.',
      questions: [
        {
          question: 'Add: (3x² + 5x - 2) + (2x² - 3x + 7)',
          solution: 'Step 1: Remove parentheses: 3x² + 5x - 2 + 2x² - 3x + 7\nStep 2: Group like terms: (3x² + 2x²) + (5x - 3x) + (-2 + 7)\nStep 3: Combine: 5x² + 2x + 5',
          answer: '5x² + 2x + 5'
        },
        {
          question: 'Subtract: (4x³ - 2x + 1) - (x³ + 5x - 3)',
          solution: 'Step 1: Distribute negative: 4x³ - 2x + 1 - x³ - 5x + 3\nStep 2: Group like terms: (4x³ - x³) + (-2x - 5x) + (1 + 3)\nStep 3: Combine: 3x³ - 7x + 4',
          answer: '3x³ - 7x + 4'
        },
        {
          question: 'Multiply: 3x²(4x³ - 2x + 5)',
          solution: 'Step 1: Distribute 3x² to each term\nStep 2: 3x² · 4x³ = 12x⁵\nStep 3: 3x² · (-2x) = -6x³\nStep 4: 3x² · 5 = 15x²\nStep 5: Result: 12x⁵ - 6x³ + 15x²',
          answer: '12x⁵ - 6x³ + 15x²'
        },
        {
          question: 'Multiply using FOIL: (x + 3)(x + 5)',
          solution: 'Step 1: First: x · x = x²\nStep 2: Outer: x · 5 = 5x\nStep 3: Inner: 3 · x = 3x\nStep 4: Last: 3 · 5 = 15\nStep 5: Combine: x² + 5x + 3x + 15 = x² + 8x + 15',
          answer: 'x² + 8x + 15'
        },
        {
          question: 'Multiply: (2x - 3)(x + 4)',
          solution: 'Step 1: First: 2x · x = 2x²\nStep 2: Outer: 2x · 4 = 8x\nStep 3: Inner: -3 · x = -3x\nStep 4: Last: -3 · 4 = -12\nStep 5: Combine: 2x² + 8x - 3x - 12 = 2x² + 5x - 12',
          answer: '2x² + 5x - 12'
        },
        {
          question: 'Simplify: 5x²y · 3xy³',
          solution: 'Step 1: Multiply coefficients: 5 · 3 = 15\nStep 2: Multiply x terms: x² · x = x³\nStep 3: Multiply y terms: y · y³ = y⁴\nStep 4: Result: 15x³y⁴',
          answer: '15x³y⁴'
        },
        {
          question: 'Expand: (x - 2)²',
          solution: 'Step 1: Rewrite: (x - 2)(x - 2)\nStep 2: FOIL: x² - 2x - 2x + 4\nStep 3: Combine: x² - 4x + 4\nAlternative: Use pattern (a - b)² = a² - 2ab + b²',
          answer: 'x² - 4x + 4'
        },
        {
          question: 'Add: (6a²b - 3ab + 2) + (-2a²b + 5ab - 7)',
          solution: 'Step 1: Remove parentheses\nStep 2: Group like terms: (6a²b - 2a²b) + (-3ab + 5ab) + (2 - 7)\nStep 3: Combine: 4a²b + 2ab - 5',
          answer: '4a²b + 2ab - 5'
        },
        {
          question: 'Multiply: (3x + 2)(2x² - x + 4)',
          solution: 'Step 1: Distribute 3x: 6x³ - 3x² + 12x\nStep 2: Distribute 2: 4x² - 2x + 8\nStep 3: Combine all: 6x³ - 3x² + 12x + 4x² - 2x + 8\nStep 4: Combine like terms: 6x³ + x² + 10x + 8',
          answer: '6x³ + x² + 10x + 8'
        },
        {
          question: 'Simplify: (2x)³',
          solution: 'Step 1: Apply power to coefficient: 2³ = 8\nStep 2: Apply power to variable: x³\nStep 3: Result: 8x³',
          answer: '8x³'
        }
      ]
    },
    {
      id: 'factoring',
      title: 'Factoring Polynomials',
      standards: ['ACT Math: Elementary Algebra', 'Algebra'],
      explanation: 'Factoring is the reverse of multiplying polynomials—expressing a polynomial as a product of simpler expressions. The ACT tests several factoring techniques: greatest common factor (GCF), factoring by grouping, factoring trinomials (including perfect square trinomials), and difference of squares (a² - b² = (a+b)(a-b)). Factoring is essential for solving quadratic equations, simplifying rational expressions, and finding zeros of functions.',
      questions: [
        {
          question: 'Factor: 6x² + 9x',
          solution: 'Step 1: Find GCF of terms: GCF(6x², 9x) = 3x\nStep 2: Factor out 3x: 3x(2x + 3)\nStep 3: Check: 3x · 2x = 6x², 3x · 3 = 9x ✓',
          answer: '3x(2x + 3)'
        },
        {
          question: 'Factor: x² + 7x + 12',
          solution: 'Step 1: Find two numbers that multiply to 12 and add to 7\nStep 2: Numbers: 3 and 4 (3 · 4 = 12, 3 + 4 = 7)\nStep 3: Factor: (x + 3)(x + 4)\nStep 4: Check: x² + 4x + 3x + 12 = x² + 7x + 12 ✓',
          answer: '(x + 3)(x + 4)'
        },
        {
          question: 'Factor: x² - 25',
          solution: 'Step 1: Recognize difference of squares: a² - b²\nStep 2: x² - 25 = x² - 5²\nStep 3: Use pattern: a² - b² = (a + b)(a - b)\nStep 4: Factor: (x + 5)(x - 5)',
          answer: '(x + 5)(x - 5)'
        },
        {
          question: 'Factor: 2x² + 7x + 3',
          solution: 'Step 1: Find factors of 2 · 3 = 6 that add to 7: 6 and 1\nStep 2: Rewrite: 2x² + 6x + 1x + 3\nStep 3: Group: (2x² + 6x) + (1x + 3)\nStep 4: Factor each: 2x(x + 3) + 1(x + 3)\nStep 5: Factor out (x + 3): (2x + 1)(x + 3)',
          answer: '(2x + 1)(x + 3)'
        },
        {
          question: 'Factor: x² - 6x + 9',
          solution: 'Step 1: Recognize perfect square trinomial: a² - 2ab + b²\nStep 2: x² - 6x + 9 = x² - 2(3)x + 3²\nStep 3: Pattern: (a - b)²\nStep 4: Factor: (x - 3)²',
          answer: '(x - 3)²'
        },
        {
          question: 'Factor: 3x³ - 12x',
          solution: 'Step 1: Find GCF: 3x\nStep 2: Factor out: 3x(x² - 4)\nStep 3: Recognize difference of squares: x² - 4 = x² - 2²\nStep 4: Factor further: 3x(x + 2)(x - 2)',
          answer: '3x(x + 2)(x - 2)'
        },
        {
          question: 'Factor: x² - 5x - 14',
          solution: 'Step 1: Find two numbers that multiply to -14 and add to -5\nStep 2: Numbers: -7 and 2 (-7 · 2 = -14, -7 + 2 = -5)\nStep 3: Factor: (x - 7)(x + 2)',
          answer: '(x - 7)(x + 2)'
        },
        {
          question: 'Factor: 4x² - 9',
          solution: 'Step 1: Recognize difference of squares: (2x)² - 3²\nStep 2: Use pattern: a² - b² = (a + b)(a - b)\nStep 3: Factor: (2x + 3)(2x - 3)',
          answer: '(2x + 3)(2x - 3)'
        },
        {
          question: 'Factor by grouping: x³ + 3x² + 2x + 6',
          solution: 'Step 1: Group: (x³ + 3x²) + (2x + 6)\nStep 2: Factor each group: x²(x + 3) + 2(x + 3)\nStep 3: Factor out common (x + 3): (x + 3)(x² + 2)',
          answer: '(x + 3)(x² + 2)'
        },
        {
          question: 'Factor: 3x² - 27',
          solution: 'Step 1: Factor out GCF: 3(x² - 9)\nStep 2: Recognize difference of squares: x² - 9 = x² - 3²\nStep 3: Factor: 3(x + 3)(x - 3)',
          answer: '3(x + 3)(x - 3)'
        }
      ]
    },
    {
      id: 'quadratic-equations',
      title: 'Solving Quadratic Equations',
      standards: ['ACT Math: Elementary Algebra', 'Algebra'],
      explanation: 'Quadratic equations have the form ax² + bx + c = 0, where a ≠ 0. The ACT tests multiple solution methods: factoring (when possible), using the quadratic formula x = [-b ± √(b² - 4ac)]/(2a), and completing the square. Quadratic equations can have two real solutions, one real solution (repeated root), or no real solutions (complex solutions). The discriminant (b² - 4ac) determines the nature of solutions: positive = 2 real, zero = 1 real, negative = no real solutions.',
      questions: [
        {
          question: 'Solve by factoring: x² + 5x + 6 = 0',
          solution: 'Step 1: Factor: (x + 2)(x + 3) = 0\nStep 2: Set each factor to zero:\nx + 2 = 0 or x + 3 = 0\nStep 3: Solve: x = -2 or x = -3',
          answer: 'x = -2 or x = -3'
        },
        {
          question: 'Solve: x² - 9 = 0',
          solution: 'Step 1: Factor difference of squares: (x + 3)(x - 3) = 0\nStep 2: Set each factor to zero:\nx + 3 = 0 or x - 3 = 0\nStep 3: Solve: x = -3 or x = 3',
          answer: 'x = -3 or x = 3'
        },
        {
          question: 'Solve: x² - 6x + 9 = 0',
          solution: 'Step 1: Factor perfect square: (x - 3)² = 0\nStep 2: Take square root: x - 3 = 0\nStep 3: Solve: x = 3\nStep 4: One solution (repeated root)',
          answer: 'x = 3'
        },
        {
          question: 'Solve using quadratic formula: x² + 4x + 1 = 0',
          solution: 'Step 1: Identify: a = 1, b = 4, c = 1\nStep 2: Formula: x = [-4 ± √(16 - 4)]/2\nStep 3: x = [-4 ± √12]/2\nStep 4: x = [-4 ± 2√3]/2\nStep 5: x = -2 ± √3',
          answer: 'x = -2 + √3 or x = -2 - √3'
        },
        {
          question: 'Solve: 2x² - 8x = 0',
          solution: 'Step 1: Factor out GCF: 2x(x - 4) = 0\nStep 2: Set each factor to zero:\n2x = 0 or x - 4 = 0\nStep 3: Solve: x = 0 or x = 4',
          answer: 'x = 0 or x = 4'
        },
        {
          question: 'Solve: x² = 16',
          solution: 'Step 1: Take square root of both sides: x = ±√16\nStep 2: x = ±4\nStep 3: Solutions: x = 4 or x = -4',
          answer: 'x = 4 or x = -4'
        },
        {
          question: 'Solve: x² + 2x - 15 = 0',
          solution: 'Step 1: Factor: (x + 5)(x - 3) = 0\nStep 2: Set each factor to zero:\nx + 5 = 0 or x - 3 = 0\nStep 3: Solve: x = -5 or x = 3',
          answer: 'x = -5 or x = 3'
        },
        {
          question: 'Solve: 3x² + 12x = 0',
          solution: 'Step 1: Factor out GCF: 3x(x + 4) = 0\nStep 2: Set each factor to zero:\n3x = 0 or x + 4 = 0\nStep 3: Solve: x = 0 or x = -4',
          answer: 'x = 0 or x = -4'
        },
        {
          question: 'Solve: (x - 2)² = 25',
          solution: 'Step 1: Take square root: x - 2 = ±5\nStep 2: Case 1: x - 2 = 5, so x = 7\nStep 3: Case 2: x - 2 = -5, so x = -3\nStep 4: Solutions: x = 7 or x = -3',
          answer: 'x = 7 or x = -3'
        },
        {
          question: 'How many real solutions does x² + 4x + 5 = 0 have?',
          solution: 'Step 1: Use discriminant: b² - 4ac\nStep 2: Calculate: 4² - 4(1)(5) = 16 - 20 = -4\nStep 3: Discriminant < 0 means no real solutions\nStep 4: Answer: 0 real solutions (2 complex)',
          answer: '0 real solutions'
        }
      ]
    }
  ]
};